import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, TextArea, Select, Alert, Card, LoadingSpinner, FileUpload } from '@/components/ui';
import { Logo } from '@/components/layout';
import { usePresets } from '@/hooks/usePresets';
import { api } from '@/lib/api';
import { clsx } from 'clsx';
import type { UploadedFile } from '@/types';
import { Edit3, Lightbulb, Clock, Brain, Bitcoin, Globe, Zap, TrendingUp, Briefcase, Heart, Newspaper, Target } from 'lucide-react';

// Map preset names to Lucide icons (no emojis)
const PRESET_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  'AI Research': Brain,
  'Crypto': Bitcoin,
  'Geopolitics': Globe,
  'Tech News': Zap,
  'Finance': TrendingUp,
  'Business': Briefcase,
  'Health': Heart,
  'General': Newspaper,
};

function getPresetIcon(presetName?: string): React.ComponentType<{ className?: string }> {
  if (!presetName) return Target;
  return PRESET_ICON_MAP[presetName] || Target;
}

const timeOptions = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, '0');
  return { value: `${hour}:00`, label: `${hour}:00` };
});

const timezoneOptions = [
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'Europe/London', label: 'London (GMT)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'UTC', label: 'UTC' },
];

// Trial users can upload up to 3 files
const TRIAL_MAX_FILES = 3;
// Character limit for custom prompts
const PROMPT_MAX_CHARS = 2000;

export function CustomizePage() {
  const navigate = useNavigate();
  const { presets, isLoading: presetsLoading } = usePresets();
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('08:00');
  const [timezone, setTimezone] = useState('America/New_York');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // File upload state
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [filesLoading, setFilesLoading] = useState(true);
  const [editablePrompt, setEditablePrompt] = useState('');
  const [promptSaved, setPromptSaved] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Ref for the prompt editing box and scroll tracking
  const promptBoxRef = useRef<HTMLDivElement>(null);
  const hasScrolledToPrompt = useRef(false);

  // Track if user selected custom prompt card
  const [isCustomMode, setIsCustomMode] = useState(false);

  // Load existing files on mount
  const loadFiles = async () => {
    try {
      const data = await api.getFiles();
      setFiles(data);
    } catch (err) {
      console.error('Failed to load files:', err);
    } finally {
      setFilesLoading(false);
    }
  };

  useEffect(() => {
    loadFiles();
  }, []);

  const handleFileUpload = async (file: File) => {
    await api.uploadFile(file);
    await loadFiles();
  };

  const handleFileDelete = async (fileId: string) => {
    await api.deleteFile(fileId);
    await loadFiles();
  };

  const handlePresetSelect = (presetId: string) => {
    setIsCustomMode(false);
    if (presetId === selectedPreset) {
      // Deselect
      setSelectedPreset(null);
      setEditablePrompt('');
      setPromptSaved(false);
    } else {
      // Select new preset
      setSelectedPreset(presetId);
      setCustomPrompt('');
      setPromptSaved(false);
      // Find the preset and pre-fill its prompt template
      const preset = presets.find(p => p.id === presetId);
      if (preset) {
        setEditablePrompt(preset.prompt_template);
      }
      // Scroll to prompt box once to guide user (only first time)
      if (!hasScrolledToPrompt.current) {
        setTimeout(() => {
          promptBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          hasScrolledToPrompt.current = true;
        }, 100);
      }
    }
  };

  const handleCustomPromptCardClick = () => {
    setSelectedPreset(null);
    setEditablePrompt('');
    setPromptSaved(false);
    setIsCustomMode(true);
    // Scroll to prompt box once
    if (!hasScrolledToPrompt.current) {
      setTimeout(() => {
        promptBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        hasScrolledToPrompt.current = true;
      }, 100);
    }
  };

  const handleSavePrompt = () => {
    // Save the edited prompt as custom prompt
    setCustomPrompt(editablePrompt);
    setPromptSaved(true);
    // Scroll to bottom to guide user
    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleSubmit = async () => {
    // Determine the final prompt to use
    const finalPrompt = selectedPreset && editablePrompt.trim()
      ? editablePrompt
      : customPrompt;

    if (!selectedPreset && !finalPrompt.trim()) {
      setError('Please select a topic or enter a custom prompt');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Save topic/prompt preferences (delivery_time/timezone kept for backward compatibility)
      await api.savePreferences({
        preset_id: selectedPreset || undefined,
        custom_prompt: finalPrompt || undefined,
        delivery_time: deliveryTime,
        timezone: timezone,
        is_active: true,
      });

      // Create delivery schedule using the new multi-schedule system
      // This ensures the user's selected time is properly stored in delivery_schedules table
      try {
        await api.addDeliverySchedule({
          delivery_time: deliveryTime,
          timezone: timezone,
          is_active: true,
        });
      } catch (scheduleErr) {
        // If schedule already exists (e.g., migrated from preferences), that's fine
        console.warn('Could not create delivery schedule (may already exist):', scheduleErr);
      }

      navigate('/thank-you');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save preferences');
    } finally {
      setIsLoading(false);
    }
  };

  if (presetsLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <Logo size="lg" linkTo="/" />
          <h1 className="text-3xl font-bold mt-6 mb-2">What would you like to learn?</h1>
          <p className="text-text-secondary">
            Choose a topic or write your own custom prompt
          </p>
        </div>

        {error && (
          <Alert variant="error" className="mb-6" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Preset Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          {presets.map((preset) => {
            const PresetIcon = getPresetIcon(preset.name);
            return (
              <Card
                key={preset.id}
                hover
                onClick={() => handlePresetSelect(preset.id)}
                className={clsx(
                  'text-center cursor-pointer transition-all',
                  selectedPreset === preset.id && 'border-primary ring-1 ring-primary'
                )}
              >
                <div className="text-4xl mb-3 flex items-center justify-center">
                  <PresetIcon className="w-10 h-10 text-primary" />
                </div>
                <h3 className="font-semibold mb-1">{preset.name}</h3>
                <p className="text-sm text-text-secondary">{preset.description}</p>
              </Card>
            );
          })}
          {/* Custom Prompt Card */}
          <Card
            hover
            onClick={handleCustomPromptCardClick}
            className={clsx(
              'text-center cursor-pointer transition-all border-dashed',
              isCustomMode && !selectedPreset && 'border-primary ring-1 ring-primary'
            )}
          >
            <div className="text-4xl mb-3 flex items-center justify-center">
              <Edit3 className="w-10 h-10 text-primary" />
            </div>
            <h3 className="font-semibold mb-1">Write Your Own</h3>
            <p className="text-sm text-text-secondary">Create a fully custom prompt</p>
          </Card>
        </div>

        {/* Editable Prompt Box - Shows when a preset is selected */}
        {selectedPreset && (
          <Card className="mb-8 border-primary/50" ref={promptBoxRef}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">
                Customize your prompt
                {promptSaved && <span className="text-primary ml-2 text-sm font-normal">✓ Saved</span>}
              </h3>
              <Button
                size="sm"
                onClick={handleSavePrompt}
                disabled={!editablePrompt.trim() || editablePrompt.length > PROMPT_MAX_CHARS}
              >
                Save
              </Button>
            </div>
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 mb-4">
              <p className="text-sm text-text-secondary flex items-start gap-2">
                <Lightbulb className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                <span>
                  <span className="font-medium text-primary">Tip:</span> Keep it simple if you'd like!
                  Our AI prompt generator will enrich your prompt and take care of the rest.
                  Your prompt can be as brief as "Latest AI news" or as detailed as you prefer.
                  You can always edit this later in your{' '}
                  <span className="text-primary font-medium">Dashboard &rarr; Settings</span>.
                </span>
              </p>
            </div>
            <TextArea
              value={editablePrompt}
              onChange={(e) => {
                if (e.target.value.length <= PROMPT_MAX_CHARS) {
                  setEditablePrompt(e.target.value);
                  setPromptSaved(false);
                }
              }}
              rows={5}
              placeholder="Enter your customized prompt..."
            />
            <div className="flex justify-end mt-2">
              <span className={clsx(
                'text-xs',
                editablePrompt.length > PROMPT_MAX_CHARS * 0.9 ? 'text-warning' : 'text-text-muted',
                editablePrompt.length >= PROMPT_MAX_CHARS && 'text-error'
              )}>
                {editablePrompt.length} / {PROMPT_MAX_CHARS}
              </span>
            </div>
          </Card>
        )}

        {/* Custom Prompt - Only show when no preset is selected */}
        {!selectedPreset && (
          <div className="mb-8" ref={!selectedPreset ? promptBoxRef : undefined}>
            {!isCustomMode && (
              <p className="text-text-secondary text-center mb-4">
                OR write your own prompt:
              </p>
            )}
            {isCustomMode && (
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 mb-4">
                <p className="text-sm text-text-secondary flex items-start gap-2">
                  <Lightbulb className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <span>
                    <span className="font-medium text-primary">Tip:</span> Keep it simple if you'd like!
                    Our AI prompt generator will enrich your prompt and take care of the rest.
                    Your prompt can be as brief as "Latest AI news" or as detailed as you prefer.
                    You can always edit this later in your{' '}
                    <span className="text-primary font-medium">Dashboard &rarr; Settings</span>.
                  </span>
                </p>
              </div>
            )}
            <TextArea
              placeholder="E.g., I want daily updates about renewable energy technology, focusing on solar and wind power advancements..."
              value={customPrompt}
              onChange={(e) => {
                if (e.target.value.length <= PROMPT_MAX_CHARS) {
                  setCustomPrompt(e.target.value);
                  if (e.target.value) setSelectedPreset(null);
                }
              }}
              rows={4}
            />
            <div className="flex justify-end mt-2">
              <span className={clsx(
                'text-xs',
                customPrompt.length > PROMPT_MAX_CHARS * 0.9 ? 'text-warning' : 'text-text-muted',
                customPrompt.length >= PROMPT_MAX_CHARS && 'text-error'
              )}>
                {customPrompt.length} / {PROMPT_MAX_CHARS}
              </span>
            </div>
          </div>
        )}

        {/* Context Files - Optional */}
        <Card className="mb-8">
          <h3 className="font-semibold mb-2">Context Files (Optional)</h3>
          <p className="text-sm text-text-secondary mb-4">
            Upload files to include as context for your newsletter research. Your AI will use these as reference material.
          </p>
          <div className="text-xs text-text-muted mb-4">
            Files: {files.length} / {TRIAL_MAX_FILES}
          </div>
          {filesLoading ? (
            <div className="flex items-center justify-center py-6">
              <LoadingSpinner size="sm" />
              <span className="ml-2 text-text-secondary">Loading files...</span>
            </div>
          ) : (
            <FileUpload
              files={files}
              onUpload={handleFileUpload}
              onDelete={handleFileDelete}
              maxFiles={TRIAL_MAX_FILES}
            />
          )}
        </Card>

        {/* Delivery Settings */}
        <Card className="mb-8">
          <h3 className="font-semibold mb-4">Delivery Settings</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Select
              label="Delivery Time"
              options={timeOptions}
              value={deliveryTime}
              onChange={(e) => setDeliveryTime(e.target.value)}
            />
            <Select
              label="Timezone"
              options={timezoneOptions}
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            />
          </div>
        </Card>

        <div className="text-center" ref={bottomRef}>
          <Button size="lg" onClick={handleSubmit} isLoading={isLoading}>
            Continue
          </Button>
          <p className="text-text-muted text-sm mt-4 flex items-center justify-center gap-2">
            <Clock className="w-4 h-4" />
            <span>Your first report will be generated after setup and typically takes 5-20 minutes due to thorough research and fact-checking.</span>
          </p>
        </div>
      </div>
    </div>
  );
}

